<script setup>
import {computed, ref} from "vue";
import generateId from "@/composables/generateId";
import IconSearch from "@/assets/icons/IconSearch.vue";
import IconCross from "@/assets/icons/IconCross.vue";


const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  alert: {
    type: String,
    default: ""
  },
  error: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["small", "medium", "large"].includes(value)
  },
  items: {
    type: Array,
    default: () => []
  },
  labelKey: {
    type: String,
    default: "label"
  },
  valueKey: {
    type: String,
    default: "value"
  },
});

const inputId = generateId();

const emit = defineEmits(["update:modelValue", "focus", "blur", "input", "change", "suffix-click", "add-item"]);

const handleAddItem = (item) => {
  emit("add-item", item);
  emit("update:modelValue", "");
};

const getItemValue = (item) => {
  if (typeof item === "object" && item !== null) {
    return item[props.valueKey] !== undefined ? item[props.valueKey] : item;
  }
  return item;
};

const getItemLabel = (item) => {
  if (typeof item === "object" && item !== null) {
    return item[props.labelKey] !== undefined ? item[props.labelKey] : String(item);
  }
  return String(item);
};

const isFocused = ref(false);
const inputRef = ref(null);

const inputWrapperClasses = computed(() => [
  `combobox-wrapper--${props.size}`,
  {
    "combobox-wrapper--focused": isFocused.value,
    "combobox-wrapper--disabled": props.disabled,
    "combobox-wrapper--error": props.error
  }
]);

const handleFocus = (event) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event) => {
  isFocused.value = false;
  emit("blur", event);
};

const clearInput = () => {
  emit("update:modelValue", "");
  emit("input", { target: { value: "" } });
  emit("change", { target: { value: "" } });
};

const localModelValue = computed(({
  set(value) {
    emit("update:modelValue", value);
  },
  get() {
    return props.modelValue;
  }
}));

const focus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus
});
</script>

<template>
  <div class="combobox-container">
    <label
      v-if="props.label"
      :for="inputId"
      class="combobox-container__label"
    >
      {{ props.label }}
      <span
        v-if="required"
        class="combobox-container__label--required"
      >*</span>
    </label>
    <div
      :class="inputWrapperClasses"
      class="combobox-wrapper"
    >
      <input
        ref="inputRef"
        v-model="localModelValue"
        class="combobox-wrapper__input"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        @focus="handleFocus"
        @blur="handleBlur"
      >
      <div
        class="combobox-wrapper__icon"
        :class="{ 'combobox-wrapper__icon--clickable': modelValue.length > 0,
                  'combobox-wrapper__icon--focused': modelValue.length === 0 && isFocused }"
        @click="modelValue.length > 0 ? clearInput() : null"
      >
        <IconSearch v-if="modelValue.length === 0" />
        <IconCross v-else />
      </div>

      <div
        v-if="props.items.length > 0 && props.modelValue.length > 0 "
        class="combobox__list-wrapper"
      >
        <div class="combobox__list scroll">
          <div
            v-for="item in props.items"
            :key="getItemValue(item)"
            class="combobox__item"
            @click="handleAddItem(item)"
          >
            {{ getItemLabel(item) }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="props.alert"
      class="combobox-container__alert"
    >
      {{ props.alert }}
    </div>
  </div>
</template>

<style scoped lang="scss">

.combobox {
  &__item {
    padding: 6px 14px;
    border-radius: 8px;

    &:hover {
      background: var(--background-bg-hover);
      cursor: pointer;
      user-select: none;
    }
  }

  &__list {
    position: relative;

    &-wrapper {
      z-index: 9999;
      position: absolute;
      top: calc(100% + 10px);
      box-sizing: border-box;
      width: 100%;
      left: 0;
      padding: 10px 8px 10px 10px;
      background: var(--background-bg-white);
      box-shadow: 1px 2px 10px 0 var(--gray-gray50);
      border-radius: 8px;
    }

    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-right: 2px;
    max-height: 200px;
  }
}

.combobox-container {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--form-primary-title-text);

    &--required { color: var(--system-red); }
  }

  &__alert {
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--form-primary-placeholder-text)
  }
}

.combobox-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: var(--background-bg-white);
  padding: 0 12px;
  border-radius: 8px;
  color: var(--form-primary-title-text);

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    color: var(--icon-icon-secondary);

    &--clickable {
      cursor: pointer;
    }

    &--focused {
      color: var(--icon-icon-on);
    }
  }

  &:hover:not(.combobox-wrapper--focused):not(.combobox-wrapper--disabled) {
    background-color: var(--background-bg-hover2);
  }

  //&--focused {
  //  background-color: var(--background-bg-hover2);
  //}

  &--error {
    border-color: var(--border-bd-error);
    background-color: var(--form-primary-default);
  }

  &--disabled {
    background-color: var(--form-primary-disabled);
    border-color: var(--border-bd-disabled);
    color: var(--text-text-disabled);
  }

  &__input {
    all: unset;
    width: 100%;
    height: 100%;
  }

  // Размеры
  &--small { height: 30px; }

  &--medium { height: 36px; }

  &--large { height: 42px; }

}

// Стили для скрола
.scroll {
  overflow-y: auto;

  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: #1A64D433 transparent;
  }

  &::-webkit-scrollbar {
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #1A64D433;
    border-radius: 30px;
  }
}

</style>